import React, { useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Divider,
  Paper,
  Stack,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  AccountBalance,
  Visibility,
  Security,
  Speed,
  Email,
  Phone,
  CheckCircle,
  Print,
  Download,
  Share,
  Image as ImageIcon,
} from '@mui/icons-material';
import html2canvas from 'html2canvas';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import { toast } from 'react-toastify';

interface WelcomeBrochureProps {
  customerName?: string;
  outstandingAmount?: number;
  onPrint?: () => void;
  onDownload?: () => void;
  onShare?: () => void;
  onExportImage?: () => void;
}

const WelcomeBrochure: React.FC<WelcomeBrochureProps> = ({
  customerName = 'Valued Customer',
  outstandingAmount,
  onPrint,
  onDownload,
  onShare,
  onExportImage,
}) => {
  const brochureRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = React.useState(false);

  const handlePrint = () => {
    window.print();
    if (onPrint) onPrint();
  };

  const handleDownload = () => {
    // Trigger print dialog for PDF download
    window.print();
    if (onDownload) onDownload();
  };

  const handleExportAsImage = async () => {
    if (!brochureRef.current) {
      toast.error('Unable to export brochure');
      return;
    }

    try {
      setExporting(true);
      toast.info('Generating image...', { autoClose: 2000 });

      // Hide action buttons during export
      const actionButtons = brochureRef.current.querySelectorAll('.no-print');
      actionButtons.forEach((btn) => {
        (btn as HTMLElement).style.display = 'none';
      });

      // Configure html2canvas options for better quality
      const canvas = await html2canvas(brochureRef.current, {
        backgroundColor: '#ffffff',
        scale: 2, // Higher quality
        useCORS: true,
        logging: false,
        width: brochureRef.current.scrollWidth,
        height: brochureRef.current.scrollHeight,
        windowWidth: brochureRef.current.scrollWidth,
        windowHeight: brochureRef.current.scrollHeight,
      });

      // Show action buttons again
      actionButtons.forEach((btn) => {
        (btn as HTMLElement).style.display = '';
      });

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        if (!blob) {
          toast.error('Failed to generate image');
          setExporting(false);
          return;
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Welcome_Brochure_${customerName.replace(/\s+/g, '_')}_${new Date().getTime()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.success('Brochure exported as image successfully! 🎉');
        if (onExportImage) onExportImage();
        setExporting(false);
      }, 'image/png', 0.95); // High quality PNG
    } catch (error) {
      console.error('Error exporting image:', error);
      toast.error('Failed to export brochure as image');
      setExporting(false);
    }
  };

  return (
    <Box
      sx={{
        '@media print': {
          '& .no-print': {
            display: 'none',
          },
          '& .print-only': {
            display: 'block',
          },
        },
        '& .print-only': {
          display: 'none',
        },
      }}
    >
      <Paper
        ref={brochureRef}
        elevation={0}
        sx={{
          p: { xs: 3, md: 6 },
          maxWidth: 800,
          mx: 'auto',
          bgcolor: 'background.paper',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.1,
          },
        }}
      >
        {/* Action Buttons - Hidden on Print */}
        <Box className="no-print" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
          <IconButton 
            onClick={handleExportAsImage} 
            color="primary" 
            size="small" 
            title="Export as Image"
            disabled={exporting}
          >
            {exporting ? <CircularProgress size={20} /> : <ImageIcon />}
          </IconButton>
          <IconButton onClick={handlePrint} color="primary" size="small" title="Print">
            <Print />
          </IconButton>
          <IconButton onClick={handleDownload} color="primary" size="small" title="Download PDF">
            <Download />
          </IconButton>
          <IconButton onClick={onShare} color="primary" size="small" title="Share">
            <Share />
          </IconButton>
        </Box>

        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 4, position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: 'white',
              mb: 2,
              boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            }}
          >
            <AccountBalance sx={{ fontSize: 40, color: 'primary.main' }} />
          </Box>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 800,
              color: 'white',
              mb: 1,
              textShadow: '0 2px 10px rgba(0,0,0,0.2)',
            }}
          >
            Welcome to Whatzneu!
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255,255,255,0.95)',
              fontWeight: 500,
            }}
          >
            Your Financial Dashboard Awaits
          </Typography>
        </Box>

        {/* Main Content Card */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            {/* Personalized Greeting */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
                Dear {customerName},
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.8, color: 'text.secondary' }}>
                We're thrilled to introduce you to our brand new <strong>Customer Portal</strong> - 
                a powerful, secure, and user-friendly platform designed to give you complete control 
                over your financial information, right at your fingertips!
              </Typography>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* What's New Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: 'primary.main', mb: 3 }}>
                🎉 What's New?
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        minWidth: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: 'primary.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Visibility sx={{ color: 'primary.main' }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        View Your Balance
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Check your outstanding balance and payment history in real-time
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        minWidth: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: 'success.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AccountBalance sx={{ color: 'success.main' }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Track Active Debits
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Monitor all your active loans and their details anytime, anywhere
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        minWidth: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: 'info.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Speed sx={{ color: 'info.main' }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Payment History
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Access complete payment records and transaction history
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Box
                      sx={{
                        minWidth: 48,
                        height: 48,
                        borderRadius: 2,
                        bgcolor: 'warning.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Security sx={{ color: 'warning.main' }} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Secure & Private
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Your data is protected with bank-level security and encryption
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Outstanding Balance Section */}
            {outstandingAmount !== undefined && (
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: 'primary.light',
                  mb: 4,
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Your Current Outstanding Balance
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: 'primary.main',
                    mb: 1,
                  }}
                >
                  {formatCurrency(outstandingAmount)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Login to view detailed breakdown and payment schedule
                </Typography>
              </Box>
            )}

            {/* How to Get Started */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: 'primary.main', mb: 3 }}>
                🚀 How to Get Started?
              </Typography>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      minWidth: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      flexShrink: 0,
                      mt: 0.5,
                    }}
                  >
                    1
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Provide Your Email
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Share your email address with us, and we'll register your account
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      minWidth: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      flexShrink: 0,
                      mt: 0.5,
                    }}
                  >
                    2
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Receive Login Credentials
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      We'll send you a secure OTP to your email for login
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      minWidth: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      flexShrink: 0,
                      mt: 0.5,
                    }}
                  >
                    3
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Access Your Dashboard
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Login at{' '}
                      <strong style={{ color: '#667eea' }}>whatzneu.com/customer/login</strong> and 
                      start managing your finances!
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* CTA Section */}
            <Box
              sx={{
                p: 4,
                borderRadius: 3,
                bgcolor: 'success.light',
                textAlign: 'center',
                mb: 4,
              }}
            >
              <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: 'success.dark' }}>
                Ready to Get Started?
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                Contact us today to register your email and unlock access to your personal dashboard!
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Email />}
                  sx={{
                    bgcolor: 'success.main',
                    '&:hover': { bgcolor: 'success.dark' },
                    px: 4,
                  }}
                >
                  Share Your Email
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Phone />}
                  sx={{ px: 4 }}
                >
                  Call Us Now
                </Button>
              </Stack>
            </Box>

            {/* Login URL Highlight */}
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                border: '2px dashed',
                borderColor: 'primary.main',
                bgcolor: 'primary.light',
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Your Login Portal
              </Typography>
              <Typography
                variant="h6"
                component="a"
                href="https://whatzneu.com/customer/login"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'primary.main',
                  fontWeight: 700,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                  display: 'block',
                  wordBreak: 'break-all',
                }}
              >
                https://whatzneu.com/customer/login
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4, position: 'relative', zIndex: 1 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            Thank you for being a valued customer!
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 1 }}>
            © {new Date().getFullYear()} Whatzneu. All rights reserved.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default WelcomeBrochure;
